import { Camera, Film, Image as ImageIcon, Clapperboard, Aperture, Plane } from "lucide-react";
import { Reveal, MaskedLine } from "./Reveal";
import { GALLERY } from "../data/gallery";

const STRIP = GALLERY;

const CAPABILITIES = [
  { icon: Film, label: "VIDEO PRODUCTION", desc: "Event films, race coverage, brand spots" },
  { icon: ImageIcon, label: "PHOTOGRAPHY", desc: "Trackside, pit lane & detail photography" },
  { icon: Clapperboard, label: "FILM & DOCUMENTARY", desc: "Narrative shorts & driver stories" },
  { icon: Camera, label: "POST-PRODUCTION", desc: "Grade, sound design & delivery" },
];

const GEAR = [
  {
    icon: Camera,
    category: "CAMERA & RIG",
    images: [
      { src: "/media/gear/fx2-rig.jpg", label: "Sony FX2 + SmallRig rig" },
    ],
    items: [
      "Sony Cinema Line FX2",
      "SmallRig Cage Rig — V-Mount, Monitor & Mic",
    ],
  },
  {
    icon: Aperture,
    category: "LENSES",
    images: [
      { src: "/media/gear/lens-2470.jpg", label: "24-70mm" },
      { src: "/media/gear/lens-70200.jpg", label: "70-200mm" },
      { src: "/media/gear/lens-14mm.jpg", label: "14mm" },
      { src: "/media/gear/lens-16mm.jpg", label: "16mm" },
      { src: "/media/gear/lens-24mm.jpg", label: "24mm" },
      { src: "/media/gear/lens-50mm.jpg", label: "50mm" },
    ],
    items: [
      "24-70mm",
      "70-200mm + Sony x2 Extender",
      "14mm",
      "16mm",
      "24mm",
      "50mm",
    ],
  },
  {
    icon: Plane,
    category: "MOVEMENT & AERIAL",
    images: [
      { src: "/media/gear/osmo3-creator.jpg", label: "Osmo Pocket 3 Creator Combo" },
      { src: "/media/gear/mavic3.jpg", label: "Mavic 3 Classic" },
      { src: "/media/gear/movmax-bladearm.jpg", label: "MovMax Blade Arm" },
      { src: "/media/gear/rs3pro.jpg", label: "DJI Ronin RS3 Pro" },
    ],
    items: [
      "DJI Osmo Pocket 3 Creator Kit + MovMax Blade Arm — rollers & moving shots",
      "DJI Mavic 3 Classic Drone — aerial capture",
      "DJI Avata 2 FPV Drone — aerial capture",
      "DJI Ronin RS3 Pro Gimbal",
    ],
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative bg-ink py-24 md:py-36">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-14 md:gap-20 items-start">
        <Reveal>
          <div className="relative group overflow-hidden letterbox aspect-[4/5] filmstrip-hover" data-testid="about-filmstrip">
            <div className="animate-filmstrip flex flex-col">
              {[...STRIP, ...STRIP].map((frame, i) => (
                <div key={i} className="pb-3 shrink-0">
                  <img
                    src={frame.thumb}
                    alt={`Pursuit Media Studios still ${String((i % STRIP.length) + 1).padStart(2, "0")}`}
                    loading="eager"
                    className="w-full aspect-[4/3] object-cover letterbox"
                  />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/40 pointer-events-none" />
            <span className="absolute bottom-5 left-5 font-mono text-[10px] tracking-[0.35em] text-white/80 bg-ink/60 backdrop-blur-sm px-3 py-1.5 border border-white/10">
              THE REEL — NEVER SLEEPS
            </span>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.4em] text-signal mb-4">05 — THE STUDIO</p>
          </Reveal>
          <h2
            data-testid="about-heading"
            className="font-display text-5xl md:text-6xl text-white leading-[0.95] mb-8"
            style={{ fontWeight: 900 }}
          >
            <MaskedLine>BUILT FOR</MaskedLine>
            <MaskedLine delay={0.1}>
              <span className="text-outline-signal">SPEED</span> STORIES
            </MaskedLine>
          </h2>

          <Reveal delay={0.1}>
            <p className="text-white/70 text-base font-light leading-relaxed mb-4">
              Pursuit Media Studios is an automotive and motorsport media agency making
              films and imagery that feel as fast as the machines in them. From time-attack
              chaos at World Time Attack Challenge, to twelve hours of endurance at
              Bathurst, to the underground culture of Unmarked — we chase the moments
              that only happen once.
            </p>
            <p className="text-smoke text-base font-light leading-relaxed mb-10">
              Every project is treated like a broadcast production: planned like telemetry,
              shot like cinema, delivered like a title sequence.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-4">
            {CAPABILITIES.map(({ icon: Icon, label, desc }, i) => (
              <Reveal key={label} delay={0.05 * i}>
                <div className="border border-white/10 bg-ink/60 p-5 hover:border-signal/70 hover:-translate-y-0.5 hover:shadow-[0_0_36px_rgba(255,42,42,0.14)] transition-all duration-500 h-full">
                  <Icon size={18} className="text-signal mb-3" />
                  <p className="font-mono text-[11px] tracking-[0.2em] text-white mb-1.5">{label}</p>
                  <p className="text-xs text-smoke font-light leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* THE GEAR WE USE — full-width band */}
      <div className="mt-20 md:mt-28 border-y border-white/10 bg-surface" data-testid="gear-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <p className="font-mono text-[10px] tracking-[0.4em] text-signal mb-4">
                  THE GEAR WE USE
                </p>
                <h3
                  className="font-display text-4xl md:text-6xl text-white leading-[0.95]"
                  style={{ fontWeight: 900 }}
                >
                  THE <span className="text-outline">HARDWARE</span>
                </h3>
              </div>
              <p className="font-mono text-[11px] tracking-[0.25em] text-smoke md:text-right">
                SONY CINEMA LINE + DJI FLIGHT SYSTEMS
                <br />
                <span className="text-white/50">EVERY FRAME SHOT ON THIS KIT</span>
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {GEAR.map(({ icon: Icon, category, items, images }, i) => (
              <Reveal key={category} delay={i * 0.06} className="h-full">
                <div
                  data-testid={`gear-card-${i + 1}`}
                  className="group relative h-full border border-white/10 bg-ink/60 rounded-2xl p-7 md:p-9 hover:border-signal/70 hover:-translate-y-1 hover:shadow-[0_0_44px_rgba(255,42,42,0.16)] transition-all duration-500 overflow-hidden"
                >
                  <span
                    className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background:
                        "radial-gradient(120% 90% at 50% 0%, rgba(255,42,42,0.14), transparent 60%)",
                    }}
                  />
                  <div className="relative overflow-hidden rounded-xl mb-6">
                    {images.length === 1 ? (
                      <img
                        src={images[0].src}
                        alt={images[0].label}
                        loading="lazy"
                        className="w-full aspect-[16/9] object-cover object-center rounded-xl transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-1.5">
                        {images.map((img) => (
                          <img
                            key={img.src}
                            src={img.src}
                            alt={img.label}
                            loading="lazy"
                            className="w-full aspect-square object-cover object-center rounded-lg transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                          />
                        ))}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/25 to-transparent pointer-events-none rounded-xl" />
                  </div>
                  <div className="flex items-center justify-between mb-6">
                    <p className="font-mono text-[11px] tracking-[0.3em] text-white">{category}</p>
                    <Icon size={20} className="text-signal" />
                  </div>
                  <ul className="space-y-3.5 relative">
                    {items.map((item) => (
                      <li
                        key={item}
                        className="text-sm font-light text-white/80 leading-relaxed flex items-start gap-3"
                      >
                        <span className="w-1.5 h-1.5 bg-signal rotate-45 shrink-0 mt-1.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
